import { Link, useNavigate, useParams } from "react-router-dom";
import { MessagesClient } from "../api/generated";
import { describeTerm } from "../components/AudienceBuilder";
import { Alert, Chip, Empty, Panel, SeverityChip, Stat, useAsync, when } from "../components/ui";

const messages = new MessagesClient("");

/**
 * Console module: <b>Read tracking</b> — what national office logs in for (§10).
 *
 * The valuable state in this system is not that a message was sent, but that a specific recipient
 * saw it. That is the whole name of the product.
 */
export function ReadTrackingPage() {
  const { id } = useParams();
  return id ? <MessageDetail id={id} /> : <MessageList />;
}

function MessageList() {
  const { data, error, loading } = useAsync(() => messages.messages_List(undefined, undefined, 100));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Read tracking</h1>
          <p className="sub">
            Every published message and how much of its audience has read it. Open one to see who
            has not, and why each recipient was included.
          </p>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      <Panel>
        {loading ? <Empty>Loading…</Empty> : (data ?? []).length === 0 ? (
          <Empty>Nothing published yet.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>#</th><th>Subject</th><th>Title</th><th>Severity</th><th>Flags</th>
                <th>Published</th><th>Audience</th><th>Read</th><th>Acknowledged</th>
              </tr>
            </thead>
            <tbody>
              {data!.map((m) => (
                <tr key={m.id}>
                  <td className="mono muted">{m.sequence}</td>
                  <td className="mono"><Link to={`/read-tracking/${m.id}`}>{m.subject}</Link></td>
                  <td>{m.title ?? <span className="muted">—</span>}</td>
                  <td><SeverityChip severity={m.severity} /></td>
                  <td>
                    {m.flags?.map((f) => <Chip key={f} kind="warn">{f}</Chip>)}
                    {m.requiresAcknowledgement && <Chip kind="on">ack required</Chip>}
                  </td>
                  <td className="muted">{when(m.publishedAt)}</td>
                  <td>{m.audienceAtPublish}</td>
                  <td>{m.readCount}</td>
                  <td>{m.requiresAcknowledgement ? m.acknowledgedCount : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

function MessageDetail({ id }: { id: string }) {
  const navigate = useNavigate();
  const { data, error, loading } = useAsync(() => messages.messages_Trace(id), [id]);

  if (loading) return <Empty>Loading…</Empty>;
  if (error) return <Alert kind="error">{error}</Alert>;
  if (!data) return null;

  const t = data.tracking!;
  const denominator = t.audienceNow ?? 0;
  const percent = denominator > 0 ? Math.round(((t.readCount ?? 0) / denominator) * 100) : 0;
  const drifted = t.audienceAtPublish !== t.audienceNow;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{data.summary?.title ?? data.summary?.subject}</h1>
          <p className="sub">
            <span className="mono">{data.summary?.subject}</span> on{" "}
            <span className="mono">{data.summary?.stream}</span>, sequence {data.summary?.sequence},
            published {when(data.summary?.publishedAt)} by {data.summary?.producerName}.
          </p>
        </div>
        <button className="ghost" onClick={() => navigate("/read-tracking")}>Back</button>
      </div>

      {t.note && <Alert kind="warn">{t.note}</Alert>}

      {data.warnings?.map((w, i) => <Alert key={i} kind="warn">{w.message}</Alert>)}

      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat
          label={t.requiresAcknowledgement ? "Audience (frozen at publish)" : "Audience now"}
          value={denominator}
          hint={drifted ? `${t.audienceAtPublish} at publish` : "unchanged since publish"}
        />
        <Stat label="Read" value={`${t.readCount} · ${percent}%`} />
        <Stat
          label={t.requiresAcknowledgement ? "Acknowledged" : "Unread"}
          value={t.requiresAcknowledgement ? t.acknowledgedCount! : t.unreadCount!}
          hint={t.requiresAcknowledgement ? `${t.unreadCount} not yet seen` : undefined}
        />
      </div>

      <div className="bar" style={{ marginBottom: 18 }}>
        <div style={{ width: `${percent}%` }} />
      </div>

      <div className="grid-2">
        <Panel title="Who has, and who has not">
          <table>
            <thead>
              <tr><th>Party</th><th>State</th><th>When</th><th>Channel</th><th></th></tr>
            </thead>
            <tbody>
              {t.recipients?.map((r) => (
                <tr key={r.partyKey}>
                  <td>
                    <span className="mono">{r.partyKey}</span>
                    <div className="muted" style={{ fontSize: 12 }}>{r.partyName}</div>
                  </td>
                  <td>
                    {r.state === "ACKNOWLEDGED" ? <Chip kind="ok">acknowledged</Chip>
                      : r.state === "READ" ? <Chip kind="ok">read</Chip>
                      : r.state === "DELIVERED" ? <Chip>delivered</Chip>
                      : <Chip kind="warn">unread</Chip>}
                  </td>
                  <td className="muted">{when(r.occurredAt)}</td>
                  <td>{r.channel ? <Chip>{r.channel}</Chip> : <span className="muted">—</span>}</td>
                  <td>
                    {r.dismissed && <Chip>dismissed</Chip>}
                    {!r.stillInAudience && <Chip kind="warn">no longer in audience</Chip>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
            Audience resolved from <span className="mono">{t.denominatorSource}</span>.
          </p>
        </Panel>

        <div>
          <Panel title="Audience — as published">
            {(["include", "require", "exclude"] as const).map((clause) => {
              const terms = data.audience?.[clause];
              if (!terms || terms.length === 0) return null;
              return (
                <div key={clause} style={{ marginBottom: 8 }}>
                  <label>{clause}</label>
                  {terms.map((term, i) => (
                    <div key={i} className="mono-block" style={{ padding: "6px 9px", marginBottom: 4 }}>
                      {describeTerm(term)}
                    </div>
                  ))}
                </div>
              );
            })}

            {data.channels && data.channels.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <label>channels</label>
                <div>{data.channels.map((c) => <Chip key={c} kind="on">{c}</Chip>)}</div>
              </div>
            )}
          </Panel>

          <Panel title="Normalized match keys">
            <table>
              <thead><tr><th>Clause</th><th>Term</th><th>Match key</th></tr></thead>
              <tbody>
                {data.terms?.map((row, i) => (
                  <tr key={i}>
                    <td>{row.clause}</td>
                    <td className="muted">{row.termNo}</td>
                    <td className="mono">{row.matchKey}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          {data.thread && data.thread.length > 1 && (
            <Panel title="Thread">
              {data.thread.map((m) => (
                <div key={m.id} style={{ paddingBottom: 10, marginBottom: 10, borderBottom: "1px solid var(--line)" }}>
                  <div className="muted" style={{ fontSize: 12 }}>
                    #{m.sequence} · {m.producer?.name} · {when(m.publishedAt)}
                    {m.causationId && " · reply"}
                  </div>
                  <div>{m.presentation?.title && <strong>{m.presentation.title} — </strong>}{m.presentation?.body}</div>
                </div>
              ))}
            </Panel>
          )}
        </div>
      </div>
    </>
  );
}
